"use client";

import { useLayoutEffect, useRef } from "react";
import { useSession } from "@sajilni/auth/react";
import {
  allowAnalyticsIdentify,
  identifyLoggedInUser,
  isAnalyticsIdentifySuppressed,
  isRealUserId,
  resetAnalytics,
} from "@/lib/analytics/identify";
import {
  AnalyticsPlatform,
  type AnalyticsEnvironment,
} from "@/lib/analytics/catalog";
import { registerAnalyticsSuperProperties } from "@/lib/analytics/super-properties";
import { useLocale } from "@sajilni/localization";

export function AnalyticsIdentity({
  environment,
}: {
  environment: AnalyticsEnvironment;
}) {
  const { data: session, status } = useSession();
  const locale = useLocale();
  const userId = session?.user?.id;
  const whiteLabelId =
    session?.whiteLabelId ?? session?.user?.whiteLabelId ?? undefined;
  const canIdentify =
    status === "authenticated" && Boolean(userId) && isRealUserId(userId ?? "");
  const wasIdentifiedRef = useRef(false);

  useLayoutEffect(() => {
    registerAnalyticsSuperProperties({
      platform: AnalyticsPlatform.Web,
      environment,
      locale,
      ...(whiteLabelId ? { white_label_id: whiteLabelId } : {}),
    });

    if (status === "loading") return;

    if (!canIdentify) {
      allowAnalyticsIdentify();
      registerAnalyticsSuperProperties({
        user_id: undefined,
        profile_id: undefined,
        participant_type: undefined,
      });
      if (wasIdentifiedRef.current) {
        wasIdentifiedRef.current = false;
        resetAnalytics();
        allowAnalyticsIdentify();
        registerAnalyticsSuperProperties({
          platform: AnalyticsPlatform.Web,
          environment,
          locale,
          ...(whiteLabelId ? { white_label_id: whiteLabelId } : {}),
          user_id: undefined,
          profile_id: undefined,
          participant_type: undefined,
        });
      }
      return;
    }

    if (isAnalyticsIdentifySuppressed()) {
      registerAnalyticsSuperProperties({
        user_id: undefined,
        profile_id: undefined,
        participant_type: undefined,
      });
      return;
    }

    if (!userId) return;

    wasIdentifiedRef.current = true;
    registerAnalyticsSuperProperties({ user_id: userId });
    identifyLoggedInUser({ id: userId });
  }, [environment, locale, status, canIdentify, userId, whiteLabelId]);

  return null;
}
